import {
  AssetType,
  EegSubsidyParametersType,
  OptimizationParameterValue,
  PVSystemDataType,
} from '@e2m-customer-portal/domains';
import { InfoTooltip, NumberInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { Button, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { equals } from 'ramda';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PVSystemProps {
  data: PVSystemDataType;
  assetType?: AssetType;
  updateValues?: (parameters: string) => void;
  identifier?: string;
  setUpdatedPVsData?: Dispatch<SetStateAction<Map<string, Map<string, OptimizationParameterValue>> | undefined>>;
  updatedPVsData?: Map<string, Map<string, OptimizationParameterValue>> | undefined;
  isSaving?: boolean;
  setSaveBarOpen?: Dispatch<SetStateAction<boolean>>;
  resetTrigger?: boolean;
  readOnly: (parameter?: string) => boolean;
}

export function PVSystem({
  data,
  updateValues,
  assetType,
  isSaving,
  updatedPVsData,
  setUpdatedPVsData,
  setSaveBarOpen,
  identifier,
  resetTrigger,
  readOnly,
}: Readonly<PVSystemProps>) {
  const [t] = useTranslation();
  const [isValid, setIsValid] = useState(false);
  const inputClass = 'w-full bg-white';

  const [updatedData, setUpdatedData] = useState<PVSystemDataType>({ ...data });
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [eegSubsidyParameters, setEegSubsidyParameters] = useState<EegSubsidyParametersType | undefined>(
    data.eeg_subsidy_parameters || undefined,
  );

  const updatePVSystemValues = (isValid: boolean) => {
    if (isValid && updateValues) {
      const updatedItems = Object.fromEntries(changes);
      const mutationBody = JSON.stringify({
        asset_type: assetType,
        pv: updatedItems,
      });

      updateValues(mutationBody);
      setChanges(new Map());
    }
  };

  const resetValues = () => {
    setUpdatedData(data);
    setChanges(new Map());
    if (setSaveBarOpen) {
      setSaveBarOpen(false);
      data.eeg_subsidy_parameters && setEegSubsidyParameters(data.eeg_subsidy_parameters);
    }
  };

  useEffect(() => {
    setUpdatedData({ ...data });
  }, [assetType]);

  useEffect(() => {
    if (
      assetType === AssetType.CoLocation &&
      eegSubsidyParameters &&
      updatedPVsData &&
      setUpdatedPVsData &&
      identifier &&
      !equals(eegSubsidyParameters, data.eeg_subsidy_parameters)
    ) {
      setUpdatedData({ ...updatedData, eeg_subsidy_parameters: eegSubsidyParameters });
      setChanges(changes.set('eeg_subsidy_parameters', eegSubsidyParameters));
      setUpdatedPVsData(updatedPVsData.set(identifier, changes));
    }
  }, [eegSubsidyParameters]);

  useEffect(() => {
    if (changes && updatedPVsData && setUpdatedPVsData && identifier) {
      setUpdatedPVsData(updatedPVsData.set(identifier, changes));
    }
  }, [changes]);

  useEffect(() => {
    if (updatedPVsData?.size === 0) {
      setChanges(new Map());
    }
  }, [updatedPVsData]);

  useEffect(resetValues, [resetTrigger]);

  if (!data) return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE_PV_SYSTEM')}</div>;

  return (
    <div
      className={clsx(
        'h-fit p-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 border-solid border border-divider rounded-md',
        assetType === AssetType.CoLocation ? 'mx-0 w-full md:w-1/2' : 'mx-6',
      )}
      data-testid="pv-system"
    >
      {assetType === AssetType.CoLocation ? (
        <h3 className="m-0 flex flex-col whitespace-nowrap xl:col-span-2">
          <span className="text-xs font-normal text-grey-300">{t('COPT_PV_TITLE')}</span>
          <span className="pt-1 text-lg font-medium">{updatedData.alias}</span>
        </h3>
      ) : (
        <div className="xl:col-span-2">
          <h3 className="m-0 whitespace-nowrap pt-1 text-lg font-medium">{t('COPT_PV_TITLE')}</h3>
          <p className="text-grey-400 text-sm font-semibold">
            {`${t('COPT_PV_DEVICE_NUMBER')} `}
            <span className="text-grey-300 font-medium">{updatedData.device_number}</span>
            <br />
            {`${t('COPT_PV_FORECAST_ID')} `}
            <span className="text-grey-300 font-medium">{updatedData.forecast_id}</span>
          </p>
        </div>
      )}
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        {updatedData.always_available !== undefined && (
          <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
            <input
              type="checkbox"
              checked={!!updatedData.always_available}
              disabled={readOnly('always_available')}
              onChange={() => {
                setSaveBarOpen?.(true);
                const always_available = !updatedData.always_available;
                setUpdatedData({ ...updatedData, always_available });
                setChanges(changes.set('always_available', always_available));
              }}
              className="w-4 h-4 mr-2 accent-green-200"
            />
            {t('COPT_PV_ALWAYS_AVAILABLE')}
            <InfoTooltip field="always_available" text={t('COPT_PV_ALWAYS_AVAILABLE_TOOLTIP')} />
          </label>
        )}
        {updatedData.installed_power !== undefined && (
          <div className="xl:col-span-2">
            <NumberInput
              value={updatedData.installed_power === null ? null : updatedData.installed_power * 1000}
              className={inputClass}
              required
              name="installed_power"
              label={t('COPT_PV_INSTALLED_POWER')}
              onChange={(value: number) => {
                setSaveBarOpen?.(true);
                const installed_power = value != null && !Number.isNaN(value) ? value / 1000 : null;
                setUpdatedData({
                  ...updatedData,
                  installed_power,
                });
                setChanges(changes.set('installed_power', installed_power));
              }}
              disabled={readOnly('installed_power')}
              notNegative
              endAdornment={<InfoTooltip field="installed_power" objectType="pv" />}
            />
          </div>
        )}
        {updatedData.accepted_power_deviation !== undefined && (
          <div className="xl:col-span-2">
            <NumberInput
              value={
                updatedData.accepted_power_deviation === null
                  ? null
                  : Math.round(updatedData.accepted_power_deviation * 100)
              }
              className={inputClass}
              required
              name="accepted_power_deviation"
              label={t('COPT_PV_ACCEPTED_POWER_DEVIATION')}
              onChange={(value: number) => {
                setSaveBarOpen?.(true);
                const accepted_power_deviation = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                setUpdatedData({
                  ...updatedData,
                  accepted_power_deviation,
                });
                setChanges(changes.set('accepted_power_deviation', accepted_power_deviation));
              }}
              disabled={readOnly('accepted_power_deviation')}
              notNegative
              endAdornment={<InfoTooltip field="accepted_power_deviation" objectType="pv" />}
            />
          </div>
        )}
        {eegSubsidyParameters && (
          <>
            <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
              {t('COPT_PV_EEG_SUBSIDY_PARAMETERS_TITLE')}
            </h4>
            {eegSubsidyParameters.eeg_auction_value !== undefined && (
              <NumberInput
                value={eegSubsidyParameters.eeg_auction_value === null ? null : eegSubsidyParameters.eeg_auction_value}
                className={inputClass}
                required
                name="eegSubsidyParameters.eeg_auction_value"
                label={t('COPT_PV_EEG_SUBSIDY_PARAMETERS_EEG_AUCTION_VALUE')}
                onChange={(value: number) => {
                  setSaveBarOpen?.(true);
                  setEegSubsidyParameters({
                    ...eegSubsidyParameters,
                    eeg_auction_value: value != null && !Number.isNaN(value) ? value : null,
                  });
                }}
                disabled={readOnly('eeg_auction_value')}
                notNegative
                endAdornment={<InfoTooltip field="eeg_auction_value" objectType="pv_eeg_subsidy_parameters" />}
              />
            )}
            {eegSubsidyParameters.negative_hours_threshold !== undefined && (
              <NumberInput
                value={
                  eegSubsidyParameters.negative_hours_threshold === null
                    ? null
                    : eegSubsidyParameters.negative_hours_threshold
                }
                className={inputClass}
                required
                name="eegSubsidyParameters.negative_hours_threshold"
                label={t('COPT_PV_EEG_SUBSIDY_PARAMETERS_NEGATIVE_HOURS_THRESHOLD')}
                onChange={(value: number) => {
                  setSaveBarOpen?.(true);
                  setEegSubsidyParameters({
                    ...eegSubsidyParameters,
                    negative_hours_threshold: value != null && !Number.isNaN(value) ? value : null,
                  });
                }}
                disabled={readOnly('negative_hours_threshold')}
                notNegative
                endAdornment={<InfoTooltip field="negative_hours_threshold" objectType="pv_eeg_subsidy_parameters" />}
              />
            )}
          </>
        )}
      </ValidatedContainer>
      {assetType !== AssetType.CoLocation && !readOnly() && (
        <div className="flex gap-2 justify-between xl:col-span-2">
          <Button color="primary" variant="outlined" className="bg-white" onClick={resetValues}>
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            color="primary"
            disabled={!isValid || isSaving}
            onClick={() => updatePVSystemValues(isValid)}
            startIcon={isSaving && <CircularProgress size={16} />}
          >
            {t('SHARED_SAVE')}
          </Button>
        </div>
      )}
    </div>
  );
}
